import { ResourceType } from "@app/core/model/resource-type";
import { SearchResourceType } from "@app/core/model/search/search-resource-type";
import { BasespaceFormControl, BasespaceFormProperties } from "../bs-app-launch-form-control";
import { ResourceReference } from "../bs-app-launch-resource-chooser-control/resource-chooser.control";
import { BsAppLaunchResourceMatcherControlComponent } from "./bs-app-launch-resource-matcher-control.component";

export interface ResourceMatcherResource {
    id: string | number;
    name: string;
    type: SearchResourceType;
}

export interface ResourceMatcherColumn {
    id: string;
    label: string;
    resourceType: string;
    resources: ResourceMatcherResource[];

    // Only for dataset matcher
    datasets?: { datasetIdToken: string; name: string  }[];

    // Only for biosamples
    // There are a bunch of other properties on this field but we don't need them so I left them out in type definition
    datasetsForBioSamples?: { libraryPrepKitName: string; libraryPrepKitId: string, bioSample: { name: string; id: string } }[][];
    bioSamples?: ResourceReference[];
}

export interface ResourceMatcherPair {
    rowIndex: number;
    columns: {
        [columnId: string]: ResourceMatcherResource | null;
    };
}

export interface ResourceMatcherFormControlProperties extends BasespaceFormProperties<BsAppLaunchResourceMatcherControlComponent> {
    columns: ResourceMatcherColumn[];
    maxRows: number;
    allowUnpairedCells: boolean;
    allowRepetition: boolean;
    searchQuery: string;
    buttonText: string;
    resourceType: SearchResourceType;
    overrideDefaultSize?: string;
}

export class ResourceMatcherFormControl extends BasespaceFormControl<BsAppLaunchResourceMatcherControlComponent> implements ResourceMatcherFormControlProperties {
    columns: ResourceMatcherColumn[];
    maxRows: number;
    allowUnpairedCells: boolean;
    allowRepetition: boolean;
    searchQuery: string;
    buttonText: string;
    resourceType: SearchResourceType;
    overrideDefaultSize?: string;

    constructor(
        data: ResourceMatcherFormControlProperties
    ) {
        const { 
            id, 
            componentReference, 
            label, 
            helpText, 
            value, 
            required, 
            requiredMessage, 
            columns,
            maxRows,
            allowUnpairedCells,
            allowRepetition,
            searchQuery,
            buttonText,
            resourceType,
        } = data;
        
        super({ 
            id, 
            componentReference, 
            helpText, 
            value, 
            required, 
            requiredMessage
        });

        this.columns = columns || [];
        this.maxRows = maxRows || 96;

        // This isn't supported server side so we will always set this to false for now. Already
        // implemented the frontend logic to support this if we ever decide to support it.
        this.allowUnpairedCells = false;

        this.allowRepetition = allowRepetition !== undefined ? allowRepetition : false;
        this.searchQuery = searchQuery || '';
        this.buttonText = buttonText || 'Paired resources';
        this.resourceType = resourceType;
        this.label = this.getReplacementText(label);
        this.overrideDefaultSize = '100%';


        const initialMatcherValue: ResourceMatcherColumn[] = JSON.parse(JSON.stringify(this.columns));

        initialMatcherValue.forEach((column) => {
            switch(this.resourceType) {
                case SearchResourceType.Sample:
                    // We don't want to populate sample chooser with extrapolated sample data from biosamples
                    // ie user submitted form with biosamples but they requeued the analysis with "input data" set to "samples..."
                    if(column.bioSamples && column.bioSamples.length > 0) {
                        initialMatcherValue.forEach((column) => {
                            column.resources = [];
                        });
                    }
                    break;
                case SearchResourceType.BiosampleLibraryPrep:
                    column.resources = [];

                    if(column.datasetsForBioSamples && column.datasetsForBioSamples.length > 0) {
                        column.datasetsForBioSamples.forEach((datasetsByBiosample) => {
                            const dataset = datasetsByBiosample[0];
                            const biosample = column.bioSamples.find((biosample) => biosample.id === dataset.bioSample.id);
                            column.resources.push({ 
                                id: `${dataset.bioSample.id}::${dataset.libraryPrepKitId}`,
                                name: `${biosample.name}::${dataset.libraryPrepKitName}`,
                                type: ResourceType.BIO_SAMPLE
                            });
                        });
                    }
                    break;
                case SearchResourceType.AppResult:
                    break;
                case SearchResourceType.Dataset:
                    column.resources = [];

                    if(column.datasets && column.datasets.length > 0) {
                        column.datasets.forEach((dataset) => {
                            column.resources.push({
                                id: dataset.datasetIdToken,
                                name: dataset.name,
                                type: ResourceType.DATASET
                            });
                        });
                    }
                    break;
            }    
        });

        this.setValue(initialMatcherValue);
    }

    getColumnFormSubmissionData(column: ResourceMatcherColumn): any {
        const serializedFormData = {
            limit: 100,
            isRepetitionAllowed: true,
            isLocked: true,
            validationRules: [],
            sorter: null,			    
            isSortAsc: true,
            isSorted: false,
            name: column.id,
            title: column.label
        };

        const resourceItems: { id: string }[] = [];
        column.resources.forEach((resource) => {
            resourceItems.push(resource ? { id: this.transformResourceIdToFormDataId(String(resource.id)) } : null);
        });
        serializedFormData['items'] = resourceItems;
        return serializedFormData;
    }

    transformResourceIdToFormDataId(id: string): string {
        switch(this.resourceType) {
            case SearchResourceType.File:
                return `dsf.${id}`;
            case SearchResourceType.Dataset:
                return `dsit.${id}`;
            default:
                return id;
        }
    }

    getReplacementText(input: string): string {
        switch(this.resourceType) {
            case SearchResourceType.Dataset:
                return input.replace("an app result", "a dataset").replace("an App Result", "a Dataset")
                    .replace("app result", "dataset").replace("App Result", "Dataset");  
            case SearchResourceType.AppResult:
                return input.replace("a dataset", "an app result").replace("a Dataset", "an App Result")
                    .replace("dataset", "app result").replace("Dataset", "App Result"); 
            case SearchResourceType.Biosample:
            case SearchResourceType.BiosampleLibraryPrep:
                // Use negative lookbehind to only replace "sample" when not preceded by "bio"
                return input.replace(/(?<!bio)sample/gi, (match) => {
                    return match === 'sample' ? 'biosample' : 'Biosample';
                });
            case SearchResourceType.Sample:
                return input.replace(/biosample/gi, (match) => {
                    return match === 'biosample' ? 'sample' : 'Sample';
                }); 
            default:
                return input;
        }
    }

    getRequestEntries(): any[][] {
        if (!this.value || !Array.isArray(this.value)) {
            return [];
        }

        const columns = this.value as ResourceMatcherColumn[];
        const serializedColumns = columns.map(col => this.getColumnFormSubmissionData(col));

        return [[this.id, JSON.stringify(serializedColumns)]];
    }
}


